// web/src/store/hedgeAccounting/hedgeAccountingSlice.ts
import { MultipleAutocompleteResponse } from '@/components/AutoComplete';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { BulkActionType } from '../components/TradeList/enums';

import {
  bulkReplaceAmortizationFromReference,
  bulkReplaceEffectivenessFromReference,
  bulkReplaceHypotheticalFromReference,
  bulkReplaceJournalEntriesFromReference,
  calculateAmortEffectiveRate,
  createAndResetJournalEntries,
  createHypoTrade,
  createJournalEntries,
  createJournalEntryMovements,
  createTransactionAmort,
  createTransactionAmortSetSchedule,
  createTransactionEffectiveness,
  createTransactionNoHedgeAcc,
  deleteTradeDesignationDate,
  deleteTransactionAmort,
  deleteTransactionAmortSetSchedule,
  deleteTransactionEffectiveness,
  editJournalEntryMovementComment,
  fetchBulkTransactionTitles,
  fetchBulkTransactionTitlesStructured,
  fetchEffectivenessTypes,
  fetchHedgeAmortData,
  fetchJournalEntries,
  fetchJournalEntryMovementStandards,
  fetchJournalEntryMovements,
  fetchJournalEntryTypes,
  fetchTransactionAmortSetSchedule,
  fetchTransactionAmortTypes,
  fetchTransactionDetail,
  fetchTransactionEffectivenessList,
  fetchTransactionLegs,
  fetchTransactionRelations,
  fetchTransactionTitle,
  fetchTransactionTitleStructured,
  fetchTransactionsWithoutHedgeAcc,
  getCompanyEntityHedgingStandard,
  getIneffectivenessValues,
  getTradeDesignationDate,
  initialAmortEffectiveRate,
  patchTransactionAmort,
  patchTransactionEffectiveness,
  postIneffectivenessValues,
  postTradeDesignationDate,
  searchTransactionAmortSetSchedule,
  updateTransactionAmort,
  updateTransactionEffectiveness,
  updateTransactionFields,
  updateTransactionNoHedgeAcc,
} from './thunks';
import {
  HedgeAccountingState,
  HedgeAmortData,
  JournalEntryMovement,
  TransactionAmortSetScheduleData,
  UpdateScheduleFieldPayload,
} from './types';

const setTradeLoading = (state: HedgeAccountingState, tradeId: string) => {
  state.tradeDataLoadingByTradeId[tradeId] = true;
  state.tradeDataErrorByTradeId[tradeId] = null;
};

const clearTradeLoading = (state: HedgeAccountingState, tradeId: string) => {
  state.tradeDataLoadingByTradeId[tradeId] = false;
};

const setTradeError = (
  state: HedgeAccountingState,
  tradeId: string,
  error: string | null,
) => {
  state.tradeDataLoadingByTradeId[tradeId] = false;
  state.tradeDataErrorByTradeId[tradeId] = error;
};

const groupJournalEntriesByTradeId = (
  rows: HedgeAccountingState['journalEntries'],
) => {
  const map: Record<string, typeof rows> = {};
  for (const row of rows) {
    const id = String(row.trade_id);
    (map[id] ??= []).push(row);
  }
  return map;
};

const groupHedgeAmortByTradeId = (
  rows: HedgeAccountingState['hedgeAmortData'],
) => {
  const map: Record<string, typeof rows> = {};
  for (const row of rows) {
    const id = String(row.transaction_id);
    (map[id] ??= []).push(row);
  }
  return map;
};

const groupEffectivenessByTradeId = (
  rows: HedgeAccountingState['transactionEffectivenessData'],
) => {
  const map: Record<string, typeof rows> = {};
  for (const row of rows) {
    const id = String(row.transaction_id);
    (map[id] ??= []).push(row);
  }
  return map;
};

const indexRelationsByTradeId = (
  rows: HedgeAccountingState['transactionRelations'],
) => {
  const map: Record<string, (typeof rows)[number]> = {};
  for (const row of rows) {
    map[String(row.trade_id)] = row;
  }
  return map;
};

const indexNoHedgeByTradeId = (
  rows: HedgeAccountingState['transactionsWithoutHedgeAcc'],
) => {
  const map: Record<string, (typeof rows)[number]> = {};
  for (const row of rows) {
    map[String(row.transaction_id)] = row;
  }
  return map;
};

const initialState: HedgeAccountingState = {
  effectivenessTypes: [],
  effectivenessTypesLoading: false,
  effectivenessTypesError: null,

  journalEntryTypes: [],
  journalEntryTypesLoading: false,
  journalEntryTypesError: null,

  transactionAmortTypes: [],
  transactionAmortTypesLoading: false,
  transactionAmortTypesError: null,

  selectedTrades: [] as MultipleAutocompleteResponse,
  selectedEntity: [] as MultipleAutocompleteResponse,

  hedgeAmortData: [],
  hedgeAmortLoading: false,
  hedgeAmortError: null,

  transactionEffectivenessData: [],
  transactionEffectivenessLoading: false,
  transactionEffectivenessError: null,

  transactionRelations: [],
  transactionRelationsLoading: false,
  transactionRelationsError: null,

  transactionsWithoutHedgeAcc: [],
  transactionsWithoutHedgeAccLoading: false,
  transactionsWithoutHedgeAccError: null,

  transactionTitle: {},
  transactionTitleLoading: false,
  transactionTitleError: null,

  tradeDesignationDate: {},
  tradeDesignationDateLoading: false,
  tradeDesignationDateError: null,

  ineffectivenessValues: {},
  ineffectivenessValuesLoading: false,
  ineffectivenessValuesError: null,

  journalEntries: [],
  journalEntriesLoading: false,
  journalEntriesError: null,

  journalEntryMovementStandards: [],
  journalEntryMovementStandardsLoading: false,
  journalEntryMovementStandardsError: null,

  journalEntryMovements: [],
  journalEntryMovementsLoading: false,
  journalEntryMovementsError: null,

  initAmortEffectiveRateResponse: null,
  initAmortEffectiveRateLoading: false,
  initAmortEffectiveRateError: null,

  companyEntityHedgingStandard: {},
  companyEntityHedgingStandardLoading: false,
  companyEntityHedgingStandardError: null,

  journalEntryCreationStatus: 'idle',
  journalEntryCreationError: null,

  transactionAmortLoading: false,
  transactionAmortError: null,

  transactionAmortSchedules: {},
  transactionAmortSchedulesLoading: false,
  transactionAmortSchedulesError: null,

  transactionAmortCalculateData: null,

  titleFilters: {
    notional: [],
    instrumentType: [],
    maturityDate: [],
    searchText: '',
  },

  transactionLegs: {},
  transactionLegsLoading: false,
  transactionLegsError: null,

  bulkTransactionTitles: [],
  bulkTransactionTitlesLoading: false,
  bulkTransactionTitlesError: null,

  bulkTransactionTitlesPagination: null,

  bulkApiFilters: {
    page_size: 20,
  },

  journalEntriesByTradeId: {},
  hedgeAmortDataByTradeId: {},
  transactionEffectivenessDataByTradeId: {},
  transactionRelationsByTradeId: {},
  transactionsWithoutHedgeAccByTradeId: {},

  tradeDataLoadingByTradeId: {},
  tradeDataErrorByTradeId: {},
  selectedTradeIds: [],

  bulkActionLoading: {},
  bulkActionError: {},
};

const hedgeAccountingSliceV2 = createSlice({
  name: 'hedgeAccounting',
  initialState,
  reducers: {
    setSelectedTrades: (
      state,
      action: PayloadAction<MultipleAutocompleteResponse>,
    ) => {
      state.selectedTrades = action.payload;
    },
    setSelectedEntity: (
      state,
      action: PayloadAction<MultipleAutocompleteResponse>,
    ) => {
      state.selectedEntity = action.payload;
    },
    clearSelections: (state) => {
      state.selectedTrades = [];
      state.selectedEntity = [];
    },
    clearTradeCache(state, action: PayloadAction<string | number>) {
      const tradeId = String(action.payload);

      delete state.journalEntriesByTradeId[tradeId];
      delete state.hedgeAmortDataByTradeId[tradeId];
      delete state.transactionEffectivenessDataByTradeId[tradeId];
      delete state.transactionRelationsByTradeId[tradeId];
      delete state.transactionsWithoutHedgeAccByTradeId[tradeId];

      delete state.tradeDataLoadingByTradeId[tradeId];
      delete state.tradeDataErrorByTradeId[tradeId];
    },
    upsertLocalAmortRow(
      state,
      action: PayloadAction<{ data: HedgeAmortData; isNew: boolean }>,
    ) {
      const { data, isNew } = action.payload;

      const index = state.hedgeAmortData.findIndex(
        (r) => r.hedge_acc_amort_id === data.hedge_acc_amort_id,
      );

      if (isNew) {
        if (index === -1) {
          state.hedgeAmortData.push(data);
        }
      } else {
        if (index !== -1) {
          state.hedgeAmortData[index] = data;
        }
      }
    },
    removeLocalAmortRow(state, action: PayloadAction<number>) {
      state.hedgeAmortData = state.hedgeAmortData.filter(
        (r) => r.hedge_acc_amort_id !== action.payload,
      );
    },
    setTransactionAmortSchedules(
      state,
      action: PayloadAction<Record<number, TransactionAmortSetScheduleData[]>>,
    ) {
      state.transactionAmortSchedules = {
        ...state.transactionAmortSchedules,
        ...action.payload,
      };
    },
    resetTransactionAmortCalculateData(state) {
      state.transactionAmortCalculateData = null;
    },
    updateScheduleField: (
      state,
      action: PayloadAction<UpdateScheduleFieldPayload>,
    ) => {
      const { amortId, scheduleId, field, value } = action.payload;

      const scheduleList = state.transactionAmortSchedules[amortId];
      if (!scheduleList) return;

      const target = scheduleList.find(
        (item) =>
          item.hedge_acc_trans_amort_set_schedule_id === Number(scheduleId),
      );

      if (target) {
        (target as any)[field] = value;
      }
    },
    setTitleFilters: (
      state,
      action: PayloadAction<{
        notional?: string[];
        instrumentType?: string[];
        maturityDate?: string[];
        searchText?: string;
      }>,
    ) => {
      state.titleFilters = {
        ...state.titleFilters,
        ...action.payload,
      };
    },
    clearTitleFilters: (state) => {
      state.titleFilters = {
        notional: [],
        instrumentType: [],
        maturityDate: [],
        searchText: '',
      };
    },
    clearTransactionTitles: (state) => {
      state.transactionTitle = {};
      state.bulkTransactionTitles = [];
      state.bulkTransactionTitlesPagination = null;
    },
    setBulkTitlesPage: (state, action: PayloadAction<number>) => {
      if (state.bulkTransactionTitlesPagination) {
        state.bulkTransactionTitlesPagination.page = action.payload;
      }
    },
    setBulkTitlesPageSize: (state, action: PayloadAction<number>) => {
      if (state.bulkTransactionTitlesPagination) {
        state.bulkTransactionTitlesPagination.page_size = action.payload;
      }
    },
    setBulkApiFilters: (
      state,
      action: PayloadAction<Partial<HedgeAccountingState['bulkApiFilters']>>,
    ) => {
      state.bulkApiFilters = {
        ...state.bulkApiFilters,
        ...action.payload,
      };
    },
    clearBulkApiFilters: (state) => {
      state.bulkApiFilters = {
        page_size: 20,
      };
    },
    setSelectedTradeId: (
      state,
      action: PayloadAction<{ tradeId: string; selected: boolean }>,
    ) => {
      const { tradeId, selected } = action.payload;

      const exists = state.selectedTradeIds.includes(tradeId);

      if (selected && !exists) {
        state.selectedTradeIds.push(tradeId);
      }

      if (!selected && exists) {
        state.selectedTradeIds = state.selectedTradeIds.filter(
          (id) => id !== tradeId,
        );
      }
    },

    toggleSelectedTradeId: (state, action: PayloadAction<string | number>) => {
      const tradeId = String(action.payload);

      const exists = state.selectedTradeIds.includes(tradeId);

      if (exists) {
        state.selectedTradeIds = state.selectedTradeIds.filter(
          (id) => id !== tradeId,
        );
        return;
      }

      state.selectedTradeIds.push(tradeId);
    },

    clearSelectedTradeIds: (state) => {
      state.selectedTradeIds = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // --- Supporting lookups ---
      .addCase(fetchJournalEntryTypes.pending, (state) => {
        state.journalEntryTypesLoading = true;
        state.journalEntryTypesError = null;
      })
      .addCase(fetchJournalEntryTypes.fulfilled, (state, action) => {
        state.journalEntryTypesLoading = false;
        state.journalEntryTypes = action.payload;
      })
      .addCase(fetchJournalEntryTypes.rejected, (state, action) => {
        state.journalEntryTypesLoading = false;
        state.journalEntryTypesError = action.payload as string;
      })

      .addCase(fetchEffectivenessTypes.pending, (state) => {
        state.effectivenessTypesLoading = true;
        state.effectivenessTypesError = null;
      })
      .addCase(fetchEffectivenessTypes.fulfilled, (state, action) => {
        state.effectivenessTypesLoading = false;
        state.effectivenessTypes = action.payload;
      })
      .addCase(fetchEffectivenessTypes.rejected, (state, action) => {
        state.effectivenessTypesLoading = false;
        state.effectivenessTypesError = action.payload as string;
      })

      .addCase(fetchTransactionAmortTypes.pending, (state) => {
        state.transactionAmortTypesLoading = true;
        state.transactionAmortTypesError = null;
      })
      .addCase(fetchTransactionAmortTypes.fulfilled, (state, action) => {
        state.transactionAmortTypesLoading = false;
        state.transactionAmortTypes = action.payload;
      })
      .addCase(fetchTransactionAmortTypes.rejected, (state, action) => {
        state.transactionAmortTypesLoading = false;
        state.transactionAmortTypesError = action.payload as string;
      })

      .addCase(fetchJournalEntryMovementStandards.pending, (state) => {
        state.journalEntryMovementStandardsLoading = true;
        state.journalEntryMovementStandardsError = null;
      })
      .addCase(
        fetchJournalEntryMovementStandards.fulfilled,
        (state, action) => {
          state.journalEntryMovementStandardsLoading = false;
          state.journalEntryMovementStandards = action.payload;
        },
      )
      .addCase(fetchJournalEntryMovementStandards.rejected, (state, action) => {
        state.journalEntryMovementStandardsLoading = false;
        state.journalEntryMovementStandardsError = action.payload as string;
      })

      // 1) fetchJournalEntries({ tradeIds })
      .addCase(fetchJournalEntries.pending, (state, action) => {
        const tradeIds = action.meta.arg?.tradeIds ?? [];
        tradeIds.forEach((id) => setTradeLoading(state, String(id)));

        state.journalEntriesLoading = true;
        state.journalEntriesError = null;
      })
      .addCase(fetchJournalEntries.fulfilled, (state, action) => {
        const tradeIds = action.meta.arg?.tradeIds ?? [];
        tradeIds.forEach((id) => clearTradeLoading(state, String(id)));

        const grouped = groupJournalEntriesByTradeId(action.payload);
        for (const id of tradeIds) {
          const key = String(id);
          state.journalEntriesByTradeId[key] = grouped[key] ?? [];
        }

        state.journalEntriesLoading = false;
        state.journalEntries = action.payload;
      })
      .addCase(fetchJournalEntries.rejected, (state, action) => {
        const tradeIds = action.meta.arg?.tradeIds ?? [];
        const err =
          (action.payload as string) || action.error.message || 'Fetch failed';

        tradeIds.forEach((id) => setTradeError(state, String(id), err));

        state.journalEntriesLoading = false;
        state.journalEntriesError = action.payload as string;
      })

      // 2) fetchHedgeAmortData({ tradeIds, runDate? })
      .addCase(fetchHedgeAmortData.pending, (state, action) => {
        const tradeIds = action.meta.arg?.tradeIds ?? [];
        tradeIds.forEach((id) => setTradeLoading(state, String(id)));

        state.hedgeAmortLoading = true;
        state.hedgeAmortError = null;
      })
      .addCase(fetchHedgeAmortData.fulfilled, (state, action) => {
        const tradeIds = action.meta.arg?.tradeIds ?? [];
        tradeIds.forEach((id) => clearTradeLoading(state, String(id)));

        const grouped = groupHedgeAmortByTradeId(action.payload);
        for (const id of tradeIds) {
          const key = String(id);
          state.hedgeAmortDataByTradeId[key] = grouped[key] ?? [];
        }

        state.hedgeAmortLoading = false;
        state.hedgeAmortData = action.payload;
      })
      .addCase(fetchHedgeAmortData.rejected, (state, action) => {
        const tradeIds = action.meta.arg?.tradeIds ?? [];
        const err =
          (action.payload as string) || action.error.message || 'Fetch failed';

        tradeIds.forEach((id) => setTradeError(state, String(id), err));

        state.hedgeAmortLoading = false;
        state.hedgeAmortError = action.payload as string;
      })

      // 3) fetchTransactionEffectivenessList({ tradeIds, runDate? })
      .addCase(fetchTransactionEffectivenessList.pending, (state, action) => {
        const tradeIds = action.meta.arg?.tradeIds ?? [];
        tradeIds.forEach((id) => setTradeLoading(state, String(id)));

        state.transactionEffectivenessLoading = true;
        state.transactionEffectivenessError = null;
      })
      .addCase(fetchTransactionEffectivenessList.fulfilled, (state, action) => {
        const tradeIds = action.meta.arg?.tradeIds ?? [];
        tradeIds.forEach((id) => clearTradeLoading(state, String(id)));

        const grouped = groupEffectivenessByTradeId(action.payload);
        for (const id of tradeIds) {
          const key = String(id);
          state.transactionEffectivenessDataByTradeId[key] = grouped[key] ?? [];
        }

        state.transactionEffectivenessLoading = false;
        state.transactionEffectivenessData = action.payload;
      })
      .addCase(fetchTransactionEffectivenessList.rejected, (state, action) => {
        const tradeIds = action.meta.arg?.tradeIds ?? [];
        const err =
          (action.payload as string) || action.error.message || 'Fetch failed';

        tradeIds.forEach((id) => setTradeError(state, String(id), err));

        state.transactionEffectivenessLoading = false;
        state.transactionEffectivenessError = action.payload as string;
      })

      // 4) fetchTransactionRelations({ tradeIds, runDate? })
      .addCase(fetchTransactionRelations.pending, (state, action) => {
        const tradeIds = action.meta.arg?.tradeIds ?? [];
        tradeIds.forEach((id) => setTradeLoading(state, String(id)));

        state.transactionRelationsLoading = true;
        state.transactionRelationsError = null;
      })
      .addCase(fetchTransactionRelations.fulfilled, (state, action) => {
        const tradeIds = action.meta.arg?.tradeIds ?? [];
        tradeIds.forEach((id) => clearTradeLoading(state, String(id)));

        const indexed = indexRelationsByTradeId(action.payload);
        for (const id of tradeIds) {
          const key = String(id);
          state.transactionRelationsByTradeId[key] = indexed[key] ?? null;
        }

        state.transactionRelationsLoading = false;
        state.transactionRelations = action.payload;
      })
      .addCase(fetchTransactionRelations.rejected, (state, action) => {
        const tradeIds = action.meta.arg?.tradeIds ?? [];
        const err =
          (action.payload as string) || action.error.message || 'Fetch failed';

        tradeIds.forEach((id) => setTradeError(state, String(id), err));

        state.transactionRelationsLoading = false;
        state.transactionRelationsError = action.payload as string;
      })

      // 5) fetchTransactionsWithoutHedgeAcc({ tradeIds, runDate? })
      .addCase(fetchTransactionsWithoutHedgeAcc.pending, (state, action) => {
        const tradeIds = action.meta.arg?.tradeIds ?? [];
        tradeIds.forEach((id) => setTradeLoading(state, String(id)));

        state.transactionsWithoutHedgeAccLoading = true;
        state.transactionsWithoutHedgeAccError = null;
      })
      .addCase(fetchTransactionsWithoutHedgeAcc.fulfilled, (state, action) => {
        const tradeIds = action.meta.arg?.tradeIds ?? [];
        tradeIds.forEach((id) => clearTradeLoading(state, String(id)));

        const indexed = indexNoHedgeByTradeId(action.payload);
        for (const id of tradeIds) {
          const key = String(id);
          state.transactionsWithoutHedgeAccByTradeId[key] =
            indexed[key] ?? null;
        }

        state.transactionsWithoutHedgeAccLoading = false;
        state.transactionsWithoutHedgeAcc = action.payload;
      })
      .addCase(fetchTransactionsWithoutHedgeAcc.rejected, (state, action) => {
        const tradeIds = action.meta.arg?.tradeIds ?? [];
        const err =
          (action.payload as string) || action.error.message || 'Fetch failed';

        tradeIds.forEach((id) => setTradeError(state, String(id), err));

        state.transactionsWithoutHedgeAccLoading = false;
        state.transactionsWithoutHedgeAccError = action.payload as string;
      })

      .addCase(initialAmortEffectiveRate.pending, (state) => {
        state.initAmortEffectiveRateLoading = true;
        state.initAmortEffectiveRateError = null;
      })
      .addCase(initialAmortEffectiveRate.fulfilled, (state, action) => {
        state.initAmortEffectiveRateLoading = false;
        state.initAmortEffectiveRateResponse = action.payload;
      })
      .addCase(initialAmortEffectiveRate.rejected, (state, action) => {
        state.initAmortEffectiveRateLoading = false;
        state.initAmortEffectiveRateError = action.payload as string;
      })

      .addCase(createTransactionAmort.pending, (state) => {
        state.transactionAmortLoading = true;
        state.transactionAmortError = null;
      })
      .addCase(createTransactionAmort.fulfilled, (state) => {
        state.transactionAmortLoading = false;
      })
      .addCase(createTransactionAmort.rejected, (state, action) => {
        state.transactionAmortLoading = false;
        state.transactionAmortError = action.payload as string;
      })

      .addCase(updateTransactionAmort.pending, (state) => {
        state.transactionAmortLoading = true;
        state.transactionAmortError = null;
      })
      .addCase(updateTransactionAmort.fulfilled, (state, action) => {
        state.transactionAmortLoading = false;
        const index = state.hedgeAmortData.findIndex(
          (item) =>
            item.hedge_acc_amort_id === action.payload.hedge_acc_amort_id,
        );
        if (index !== -1) {
          state.hedgeAmortData[index] = action.payload;
        }
      })
      .addCase(updateTransactionAmort.rejected, (state, action) => {
        state.transactionAmortLoading = false;
        state.transactionAmortError = action.payload as string;
      })

      .addCase(patchTransactionAmort.pending, (state) => {
        state.transactionAmortLoading = true;
        state.transactionAmortError = null;
      })
      .addCase(patchTransactionAmort.fulfilled, (state, action) => {
        state.transactionAmortLoading = false;
        const index = state.hedgeAmortData.findIndex(
          (item) =>
            item.hedge_acc_amort_id === action.payload.hedge_acc_amort_id,
        );
        if (index !== -1) {
          state.hedgeAmortData[index] = action.payload;
        }
      })
      .addCase(patchTransactionAmort.rejected, (state, action) => {
        state.transactionAmortLoading = false;
        state.transactionAmortError = action.payload as string;
      })

      .addCase(deleteTransactionAmort.pending, (state) => {
        state.transactionAmortLoading = true;
        state.transactionAmortError = null;
      })
      .addCase(deleteTransactionAmort.fulfilled, (state, action) => {
        state.transactionAmortLoading = false;
        state.hedgeAmortData = state.hedgeAmortData.filter(
          (item) => item.hedge_acc_amort_id !== action.payload,
        );
      })
      .addCase(deleteTransactionAmort.rejected, (state, action) => {
        state.transactionAmortLoading = false;
        state.transactionAmortError = action.payload as string;
      })

      .addCase(calculateAmortEffectiveRate.pending, (state) => {
        state.transactionAmortLoading = true;
        state.transactionAmortError = null;
      })
      .addCase(calculateAmortEffectiveRate.fulfilled, (state, action) => {
        state.transactionAmortLoading = false;
        state.transactionAmortCalculateData = action.payload;
      })
      .addCase(calculateAmortEffectiveRate.rejected, (state, action) => {
        state.transactionAmortLoading = false;
        state.transactionAmortError = action.payload as string;
      })

      .addCase(fetchTransactionAmortSetSchedule.pending, (state) => {
        state.transactionAmortSchedulesLoading = true;
        state.transactionAmortSchedulesError = null;
      })
      .addCase(fetchTransactionAmortSetSchedule.fulfilled, (state, action) => {
        state.transactionAmortSchedulesLoading = false;
        if (action.meta.arg) {
          state.transactionAmortSchedules[action.meta.arg] = action.payload;
        }
      })
      .addCase(fetchTransactionAmortSetSchedule.rejected, (state, action) => {
        state.transactionAmortSchedulesLoading = false;
        state.transactionAmortSchedulesError = action.payload as string;
      })

      .addCase(createTransactionAmortSetSchedule.pending, (state) => {
        state.transactionAmortSchedulesLoading = true;
        state.transactionAmortSchedulesError = null;
      })
      .addCase(createTransactionAmortSetSchedule.fulfilled, (state) => {
        state.transactionAmortSchedulesLoading = false;
      })
      .addCase(createTransactionAmortSetSchedule.rejected, (state, action) => {
        state.transactionAmortSchedulesLoading = false;
        state.transactionAmortSchedulesError = action.payload as string;
      })

      .addCase(searchTransactionAmortSetSchedule.pending, (state) => {
        state.transactionAmortSchedulesLoading = true;
        state.transactionAmortSchedulesError = null;
      })
      .addCase(searchTransactionAmortSetSchedule.fulfilled, (state, action) => {
        state.transactionAmortSchedulesLoading = false;
        state.transactionAmortSchedules = {
          ...state.transactionAmortSchedules,
          ...action.payload,
        };
      })
      .addCase(searchTransactionAmortSetSchedule.rejected, (state, action) => {
        state.transactionAmortSchedulesLoading = false;
        state.transactionAmortSchedulesError = action.payload as string;
      })

      .addCase(createTransactionEffectiveness.pending, (state) => {
        state.transactionEffectivenessLoading = true;
        state.transactionEffectivenessError = null;
      })
      .addCase(createTransactionEffectiveness.fulfilled, (state, action) => {
        state.transactionEffectivenessLoading = false;
        if (action.payload) {
          state.transactionEffectivenessData.push(action.payload);
        }
      })
      .addCase(createTransactionEffectiveness.rejected, (state, action) => {
        state.transactionEffectivenessLoading = false;
        state.transactionEffectivenessError = action.payload as string;
      })

      .addCase(updateTransactionEffectiveness.pending, (state) => {
        state.transactionEffectivenessLoading = true;
        state.transactionEffectivenessError = null;
      })
      .addCase(updateTransactionEffectiveness.fulfilled, (state, action) => {
        state.transactionEffectivenessLoading = false;
        const index = state.transactionEffectivenessData.findIndex(
          (item) =>
            item.hedge_acc_trans_eff_id ===
            action.payload.hedge_acc_trans_eff_id,
        );
        if (index !== -1) {
          state.transactionEffectivenessData[index] = action.payload;
        }
      })
      .addCase(updateTransactionEffectiveness.rejected, (state, action) => {
        state.transactionEffectivenessLoading = false;
        state.transactionEffectivenessError = action.payload as string;
      })

      .addCase(patchTransactionEffectiveness.pending, (state) => {
        state.transactionEffectivenessLoading = true;
        state.transactionEffectivenessError = null;
      })
      .addCase(patchTransactionEffectiveness.fulfilled, (state, action) => {
        state.transactionEffectivenessLoading = false;
        const index = state.transactionEffectivenessData.findIndex(
          (item) =>
            item.hedge_acc_trans_eff_id ===
            action.payload.hedge_acc_trans_eff_id,
        );
        if (index !== -1) {
          state.transactionEffectivenessData[index] = action.payload;
        }
      })
      .addCase(patchTransactionEffectiveness.rejected, (state, action) => {
        state.transactionEffectivenessLoading = false;
        state.transactionEffectivenessError = action.payload as string;
      })

      .addCase(deleteTransactionEffectiveness.pending, (state) => {
        state.transactionEffectivenessLoading = true;
        state.transactionEffectivenessError = null;
      })
      .addCase(deleteTransactionEffectiveness.fulfilled, (state, action) => {
        state.transactionEffectivenessLoading = false;
        state.transactionEffectivenessData =
          state.transactionEffectivenessData.filter(
            (item) => item.hedge_acc_trans_eff_id !== action.payload,
          );
      })
      .addCase(deleteTransactionEffectiveness.rejected, (state, action) => {
        state.transactionEffectivenessLoading = false;
        state.transactionEffectivenessError =
          action.payload || 'Failed to delete transaction effectiveness';
      })

      .addCase(fetchTransactionTitle.pending, (state) => {
        state.transactionTitleLoading = true;
        state.transactionTitleError = null;
      })
      .addCase(fetchTransactionTitle.fulfilled, (state, action) => {
        state.transactionTitleLoading = false;
        state.transactionTitle[action.meta.arg.transactionId] = {
          title: action.payload,
        };
      })
      .addCase(fetchTransactionTitle.rejected, (state, action) => {
        state.transactionTitleLoading = false;
        state.transactionTitleError =
          action.payload || 'Failed to fetch transaction title';
      })

      .addCase(fetchBulkTransactionTitles.pending, (state) => {
        state.bulkTransactionTitlesLoading = true;
        state.bulkTransactionTitlesError = null;
      })
      .addCase(fetchBulkTransactionTitles.fulfilled, (state, action) => {
        state.bulkTransactionTitlesLoading = false;
        state.bulkTransactionTitles = action.payload.transaction_titles;
        state.bulkTransactionTitlesPagination = {
          page: action.payload.page,
          page_size: action.payload.page_size,
          total_transactions: action.payload.total_transactions,
          total_pages: action.payload.total_pages,
          transactions_in_page: action.payload.transactions_in_page,
          processing_time_seconds: action.payload.processing_time_seconds,
        };
      })
      .addCase(fetchBulkTransactionTitles.rejected, (state, action) => {
        state.bulkTransactionTitlesLoading = false;
        state.bulkTransactionTitlesError =
          action.payload || 'Failed to fetch bulk transaction titles';
      })

      .addCase(fetchTransactionDetail.pending, (state) => {
        state.bulkTransactionTitlesLoading = true;
        state.bulkTransactionTitlesError = null;
      })
      .addCase(fetchTransactionDetail.fulfilled, (state, action) => {
        state.bulkTransactionTitlesLoading = false;
        const existingIndex = state.bulkTransactionTitles.findIndex(
          (t) => t.transaction_id === action.payload.transaction_id,
        );
        if (existingIndex >= 0) {
          state.bulkTransactionTitles[existingIndex] = action.payload;
        } else {
          state.bulkTransactionTitles.push(action.payload);
        }
      })
      .addCase(fetchTransactionDetail.rejected, (state, action) => {
        state.bulkTransactionTitlesLoading = false;
        state.bulkTransactionTitlesError =
          action.payload || 'Failed to fetch transaction detail';
      })

      .addCase(fetchTransactionTitleStructured.pending, (state) => {
        state.bulkTransactionTitlesLoading = true;
        state.bulkTransactionTitlesError = null;
      })
      .addCase(fetchTransactionTitleStructured.fulfilled, (state, action) => {
        state.bulkTransactionTitlesLoading = false;
        const existingIndex = state.bulkTransactionTitles.findIndex(
          (t) => t.transaction_id === action.payload.transaction_id,
        );
        if (existingIndex >= 0) {
          state.bulkTransactionTitles[existingIndex] = action.payload;
        } else {
          state.bulkTransactionTitles.push(action.payload);
        }
      })
      .addCase(fetchTransactionTitleStructured.rejected, (state, action) => {
        state.bulkTransactionTitlesLoading = false;
        state.bulkTransactionTitlesError =
          action.payload || 'Failed to fetch transaction title structured';
      })

      .addCase(fetchBulkTransactionTitlesStructured.pending, (state) => {
        state.bulkTransactionTitlesLoading = true;
        state.bulkTransactionTitlesError = null;
      })
      .addCase(
        fetchBulkTransactionTitlesStructured.fulfilled,
        (state, action) => {
          state.bulkTransactionTitlesLoading = false;
          state.bulkTransactionTitles = action.payload;
        },
      )
      .addCase(
        fetchBulkTransactionTitlesStructured.rejected,
        (state, action) => {
          state.bulkTransactionTitlesLoading = false;
          state.bulkTransactionTitlesError =
            action.payload ||
            'Failed to fetch bulk transaction titles structured';
        },
      )

      .addCase(fetchTransactionLegs.pending, (state) => {
        state.transactionLegsLoading = true;
        state.transactionLegsError = null;
      })
      .addCase(fetchTransactionLegs.fulfilled, (state, action) => {
        state.transactionLegsLoading = false;
        state.transactionLegs[action.payload.transactionId] =
          action.payload.legs;
      })
      .addCase(fetchTransactionLegs.rejected, (state, action) => {
        state.transactionLegsLoading = false;
        state.transactionLegsError =
          action.payload || 'Failed to fetch transaction legs';
      })

      .addCase(getTradeDesignationDate.pending, (state) => {
        state.tradeDesignationDateLoading = true;
        state.tradeDesignationDateError = null;
      })
      .addCase(getTradeDesignationDate.fulfilled, (state, action) => {
        state.tradeDesignationDateLoading = false;
        if (action.payload && action.payload.transaction_id) {
          state.tradeDesignationDate[action.payload.transaction_id] = {
            transaction_id: action.payload.transaction_id,
            designation_date: action.payload.designation_date,
          };
        }
      })
      .addCase(getTradeDesignationDate.rejected, (state, action) => {
        state.tradeDesignationDateLoading = false;
        state.tradeDesignationDateError =
          action.payload || 'Failed to fetch trade designation date';
      })

      .addCase(postTradeDesignationDate.pending, (state) => {
        state.tradeDesignationDateLoading = true;
        state.tradeDesignationDateError = null;
      })
      .addCase(postTradeDesignationDate.fulfilled, (state, action) => {
        state.tradeDesignationDateLoading = false;
        const transactionId = action.meta.arg.transaction_id;
        const designationDate = action.meta.arg.designation_date;
        state.tradeDesignationDate[transactionId] = {
          transaction_id: transactionId,
          designation_date: designationDate,
        };
      })
      .addCase(postTradeDesignationDate.rejected, (state, action) => {
        state.tradeDesignationDateLoading = false;
        state.tradeDesignationDateError =
          action.payload || 'Failed to set trade designation date';
      })

      .addCase(deleteTradeDesignationDate.pending, (state) => {
        state.tradeDesignationDateLoading = true;
        state.tradeDesignationDateError = null;
      })
      .addCase(deleteTradeDesignationDate.fulfilled, (state, action) => {
        state.tradeDesignationDateLoading = false;
        if (action.meta.arg) {
          delete state.tradeDesignationDate[action.meta.arg];
        }
      })
      .addCase(deleteTradeDesignationDate.rejected, (state, action) => {
        state.tradeDesignationDateLoading = false;
        state.tradeDesignationDateError =
          action.payload || 'Failed to delete trade designation date';
      })

      .addCase(getIneffectivenessValues.pending, (state) => {
        state.ineffectivenessValuesLoading = true;
        state.ineffectivenessValuesError = null;
      })
      .addCase(getIneffectivenessValues.fulfilled, (state, action) => {
        state.ineffectivenessValuesLoading = false;
        if (action.payload && action.meta.arg) {
          state.ineffectivenessValues[action.meta.arg] = action.payload;
        }
      })
      .addCase(getIneffectivenessValues.rejected, (state, action) => {
        state.ineffectivenessValuesLoading = false;
        state.ineffectivenessValuesError =
          action.error.message || 'Failed to fetch ineffectiveness values';
      })
      .addCase(postIneffectivenessValues.pending, (state) => {
        state.ineffectivenessValuesLoading = true;
        state.ineffectivenessValuesError = null;
      })
      .addCase(postIneffectivenessValues.fulfilled, (state, action) => {
        state.ineffectivenessValuesLoading = false;
        if (action.payload && action.meta.arg.transactionId) {
          state.ineffectivenessValues[action.meta.arg.transactionId] =
            action.payload;
        }
      })
      .addCase(postIneffectivenessValues.rejected, (state, action) => {
        state.ineffectivenessValuesLoading = false;
        state.ineffectivenessValuesError =
          action.error.message || 'Failed to post ineffectiveness values';
      })

      .addCase(fetchJournalEntryMovements.pending, (state) => {
        state.journalEntryMovementsLoading = true;
        state.journalEntryMovementsError = null;
      })
      .addCase(
        fetchJournalEntryMovements.fulfilled,
        (state, action: PayloadAction<JournalEntryMovement[]>) => {
          state.journalEntryMovementsLoading = false;
          state.journalEntryMovements = action.payload;
        },
      )
      .addCase(fetchJournalEntryMovements.rejected, (state, action) => {
        state.journalEntryMovementsLoading = false;
        state.journalEntryMovementsError =
          action.payload || action.error.message || null;
      })

      .addCase(createJournalEntryMovements.pending, (state) => {
        state.journalEntryMovementsLoading = true;
        state.journalEntryMovementsError = null;
      })
      .addCase(
        createJournalEntryMovements.fulfilled,
        (state, _action: PayloadAction<string>) => {
          state.journalEntryMovementsLoading = false;
        },
      )
      .addCase(createJournalEntryMovements.rejected, (state, action) => {
        state.journalEntryMovementsLoading = false;
        state.journalEntryMovementsError =
          action.payload || action.error.message || null;
      })

      .addCase(editJournalEntryMovementComment.pending, (state) => {
        state.journalEntryMovementsLoading = true;
      })
      .addCase(
        editJournalEntryMovementComment.fulfilled,
        (state, _action: PayloadAction<string>) => {
          state.journalEntryMovementsLoading = false;
        },
      )
      .addCase(editJournalEntryMovementComment.rejected, (state, action) => {
        state.journalEntryMovementsLoading = false;
        state.journalEntryMovementsError =
          action.error.message || 'Failed to edit movement comment';
      })

      .addCase(getCompanyEntityHedgingStandard.pending, (state) => {
        state.companyEntityHedgingStandardLoading = true;
        state.companyEntityHedgingStandardError = null;
      })
      .addCase(getCompanyEntityHedgingStandard.fulfilled, (state, action) => {
        state.companyEntityHedgingStandardLoading = false;
        if (action.meta.arg) {
          state.companyEntityHedgingStandard[action.meta.arg] = action.payload;
        }
      })
      .addCase(getCompanyEntityHedgingStandard.rejected, (state, action) => {
        state.companyEntityHedgingStandardLoading = false;
        state.companyEntityHedgingStandardError =
          action.payload || 'Failed to fetch company entity hedging standard';
      })

      .addCase(createJournalEntries.pending, (state) => {
        state.journalEntriesLoading = true;
        state.journalEntriesError = null;
      })
      .addCase(createJournalEntries.fulfilled, (state, action) => {
        state.journalEntriesLoading = false;
        if (action.payload && action.payload.length > 0) {
          const newTradeIds = action.payload.map((entry) => entry.trade_id);
          const filteredEntries = state.journalEntries.filter(
            (entry) => !newTradeIds.includes(entry.trade_id),
          );
          state.journalEntries = [...filteredEntries, ...action.payload];
        }
      })
      .addCase(createJournalEntries.rejected, (state, action) => {
        state.journalEntriesLoading = false;
        state.journalEntriesError = action.payload as string;
      })

      .addCase(createTransactionNoHedgeAcc.pending, (state) => {
        state.transactionsWithoutHedgeAccLoading = true;
        state.transactionsWithoutHedgeAccError = null;
      })
      .addCase(createTransactionNoHedgeAcc.fulfilled, (state, action) => {
        state.transactionsWithoutHedgeAccLoading = false;
        const existingIndex = state.transactionsWithoutHedgeAcc.findIndex(
          (item) => item.transaction_id === action.payload.transaction_id,
        );

        if (existingIndex >= 0) {
          state.transactionsWithoutHedgeAcc[existingIndex] = action.payload;
        } else {
          state.transactionsWithoutHedgeAcc.push(action.payload);
        }
      })
      .addCase(createTransactionNoHedgeAcc.rejected, (state, action) => {
        state.transactionsWithoutHedgeAccLoading = false;
        state.transactionsWithoutHedgeAccError = action.payload as string;
      })

      .addCase(updateTransactionNoHedgeAcc.pending, (state) => {
        state.transactionsWithoutHedgeAccLoading = true;
        state.transactionsWithoutHedgeAccError = null;
      })
      .addCase(updateTransactionNoHedgeAcc.fulfilled, (state, action) => {
        state.transactionsWithoutHedgeAccLoading = false;
        const existingIndex = state.transactionsWithoutHedgeAcc.findIndex(
          (item) => item.transaction_id === action.payload.transaction_id,
        );

        if (existingIndex >= 0) {
          state.transactionsWithoutHedgeAcc[existingIndex] = action.payload;
        } else {
          state.transactionsWithoutHedgeAcc.push(action.payload);
        }
      })
      .addCase(updateTransactionNoHedgeAcc.rejected, (state, action) => {
        state.transactionsWithoutHedgeAccLoading = false;
        state.transactionsWithoutHedgeAccError = action.payload as string;
      })

      .addCase(createAndResetJournalEntries.pending, (state) => {
        state.journalEntryCreationStatus = 'loading';
        state.journalEntryCreationError = null;
      })
      .addCase(createAndResetJournalEntries.fulfilled, (state) => {
        state.journalEntryCreationStatus = 'succeeded';
      })
      .addCase(createAndResetJournalEntries.rejected, (state, action) => {
        state.journalEntryCreationStatus = 'failed';
        state.journalEntryCreationError =
          action.error.message || 'Failed to create journal entries';
      })

      .addCase(deleteTransactionAmortSetSchedule.pending, (state) => {
        state.transactionAmortSchedulesLoading = true;
        state.transactionAmortSchedulesError = null;
      })
      .addCase(deleteTransactionAmortSetSchedule.fulfilled, (state) => {
        state.transactionAmortSchedulesLoading = false;
      })
      .addCase(deleteTransactionAmortSetSchedule.rejected, (state, action) => {
        state.transactionAmortSchedulesLoading = false;
        state.transactionAmortSchedulesError = action.payload as string;
      })

      .addCase(updateTransactionFields.pending, (state) => {
        state.bulkTransactionTitlesLoading = true;
        state.bulkTransactionTitlesError = null;
      })
      .addCase(updateTransactionFields.fulfilled, (state, action) => {
        state.bulkTransactionTitlesLoading = false;
        const existingIndex = state.bulkTransactionTitles.findIndex(
          (t) => t.transaction_id === action.payload.transaction_id,
        );
        if (existingIndex >= 0) {
          state.bulkTransactionTitles[existingIndex] = action.payload;
        } else {
          state.bulkTransactionTitles.push(action.payload);
        }
      })
      .addCase(updateTransactionFields.rejected, (state, action) => {
        state.bulkTransactionTitlesLoading = false;
        state.bulkTransactionTitlesError =
          action.payload || 'Failed to update transaction fields';
      })
      .addCase(bulkReplaceJournalEntriesFromReference.pending, (state) => {
        state.bulkActionLoading[BulkActionType.JournalEntries] = true;
        state.bulkActionError[BulkActionType.JournalEntries] = null;
      })
      .addCase(bulkReplaceJournalEntriesFromReference.fulfilled, (state) => {
        state.bulkActionLoading[BulkActionType.JournalEntries] = false;
      })
      .addCase(
        bulkReplaceJournalEntriesFromReference.rejected,
        (state, action) => {
          state.bulkActionLoading[BulkActionType.JournalEntries] = false;
          state.bulkActionError[BulkActionType.JournalEntries] =
            (action.payload as string) ||
            action.error.message ||
            'Failed to apply bulk journal entries';
        },
      )
      .addCase(bulkReplaceEffectivenessFromReference.pending, (state) => {
        state.bulkActionLoading[BulkActionType.Effectiveness] = true;
        state.bulkActionError[BulkActionType.Effectiveness] = null;
      })
      .addCase(bulkReplaceEffectivenessFromReference.fulfilled, (state) => {
        state.bulkActionLoading[BulkActionType.Effectiveness] = false;
      })
      .addCase(
        bulkReplaceEffectivenessFromReference.rejected,
        (state, action) => {
          state.bulkActionLoading[BulkActionType.Effectiveness] = false;
          state.bulkActionError[BulkActionType.Effectiveness] =
            (action.payload as string) ||
            action.error.message ||
            'Failed to apply bulk effectiveness';
        },
      )

      .addCase(bulkReplaceHypotheticalFromReference.pending, (state) => {
        state.bulkActionLoading[BulkActionType.Hypothetical] = true;
        state.bulkActionError[BulkActionType.Hypothetical] = null;
      })
      .addCase(bulkReplaceHypotheticalFromReference.fulfilled, (state) => {
        state.bulkActionLoading[BulkActionType.Hypothetical] = false;
      })
      .addCase(
        bulkReplaceHypotheticalFromReference.rejected,
        (state, action) => {
          state.bulkActionLoading[BulkActionType.Hypothetical] = false;
          state.bulkActionError[BulkActionType.Hypothetical] =
            (action.payload as string) ||
            action.error.message ||
            'Failed to apply bulk hypothetical';
        },
      )
      .addCase(bulkReplaceAmortizationFromReference.pending, (state) => {
        state.bulkActionLoading[BulkActionType.Amortization] = true;
        state.bulkActionError[BulkActionType.Amortization] = null;
      })
      .addCase(bulkReplaceAmortizationFromReference.fulfilled, (state) => {
        state.bulkActionLoading[BulkActionType.Amortization] = false;
      })
      .addCase(
        bulkReplaceAmortizationFromReference.rejected,
        (state, action) => {
          state.bulkActionLoading[BulkActionType.Amortization] = false;
          state.bulkActionError[BulkActionType.Amortization] =
            (action.payload as string) ||
            action.error.message ||
            'Failed to apply bulk amortization';
        },
      );
  },
});

export const {
  setSelectedTrades,
  setSelectedEntity,
  clearSelections,
  clearTradeCache,
  upsertLocalAmortRow,
  removeLocalAmortRow,
  setTransactionAmortSchedules,
  resetTransactionAmortCalculateData,
  updateScheduleField,
  setTitleFilters,
  clearTitleFilters,
  clearTransactionTitles,
  setBulkTitlesPage,
  setBulkTitlesPageSize,
  setBulkApiFilters,
  clearBulkApiFilters,
  setSelectedTradeId,
  clearSelectedTradeIds,
} = hedgeAccountingSliceV2.actions;

export {
  fetchJournalEntries,
  fetchJournalEntryTypes,
  fetchEffectivenessTypes,
  fetchTransactionAmortTypes,
  fetchJournalEntryMovementStandards,
  fetchHedgeAmortData,
  initialAmortEffectiveRate,
  createTransactionAmort,
  updateTransactionAmort,
  patchTransactionAmort,
  deleteTransactionAmort,
  calculateAmortEffectiveRate,
  fetchTransactionAmortSetSchedule,
  createTransactionAmortSetSchedule,
  searchTransactionAmortSetSchedule,
  fetchTransactionEffectivenessList,
  createTransactionEffectiveness,
  updateTransactionEffectiveness,
  patchTransactionEffectiveness,
  deleteTransactionEffectiveness,
  fetchTransactionRelations,
  fetchTransactionsWithoutHedgeAcc,
  createTransactionNoHedgeAcc,
  updateTransactionNoHedgeAcc,
  updateTransactionFields,
  fetchTransactionTitle,
  fetchBulkTransactionTitles,
  fetchTransactionDetail,
  fetchTransactionTitleStructured,
  fetchBulkTransactionTitlesStructured,
  fetchTransactionLegs,
  createHypoTrade,
  getTradeDesignationDate,
  postTradeDesignationDate,
  deleteTradeDesignationDate,
  getIneffectivenessValues,
  postIneffectivenessValues,
  fetchJournalEntryMovements,
  createJournalEntryMovements,
  editJournalEntryMovementComment,
  getCompanyEntityHedgingStandard,
  createJournalEntries,
  createAndResetJournalEntries,
};

export default hedgeAccountingSliceV2.reducer;
